<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from '@/components/AppHeader.vue';
import AppSidebar from '@/components/AppSidebar.vue';
import AppFooter from '@/components/AppFooter.vue';

const route = useRoute();
const isFillRoute = computed(() => Boolean(route.meta.fillHeight));
</script>

<template>
  <div class="app-shell" :class="{ 'app-shell--locked': isFillRoute }">
    <AppHeader />
    <div class="app-shell__body">
      <AppSidebar />
      <main class="app-shell__main" :class="{ 'app-shell__main--fill': isFillRoute }">
        <RouterView v-slot="{ Component, route }">
          <component
            :is="Component"
            :class="{ 'route-view--fill': route.meta.fillHeight }"
          />
        </RouterView>
      </main>
    </div>
    <AppFooter v-if="!isFillRoute" />
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100%;
  min-height: 100dvh;
  min-height: -webkit-fill-available;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(circle at top left, rgba(251, 113, 133, 0.12), transparent 28%),
    linear-gradient(180deg, #fff7fb 0%, #fdf2f8 100%);
  padding-left: env(safe-area-inset-left, 0);
  padding-right: env(safe-area-inset-right, 0);
}

.app-shell--locked {
  height: 100%;
  height: 100dvh;
  height: -webkit-fill-available;
  max-height: 100dvh;
  max-height: -webkit-fill-available;
  min-height: 0;
  overflow: hidden;
}

.app-shell__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.app-shell--locked .app-shell__body {
  overflow: hidden;
}

.app-shell__main {
  flex: 1;
  padding: 1.25rem;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior-y: contain;
}

/* Android Chrome: nested flex+overflow scroll often breaks — use window scroll on mobile */
@media (max-width: 900px) {
  .app-shell:not(.app-shell--locked) {
    height: auto;
    min-height: 100dvh;
    min-height: -webkit-fill-available;
    overflow: visible;
  }

  .app-shell:not(.app-shell--locked) .app-shell__body {
    flex: 1 1 auto;
    min-height: auto;
    overflow: visible;
  }

  .app-shell:not(.app-shell--locked) .app-shell__main {
    flex: 1 1 auto;
    min-height: auto;
    overflow: visible;
    display: block;
  }
}

.app-shell__main--fill {
  padding: 0.65rem 0.85rem;
  padding-bottom: max(0.65rem, env(safe-area-inset-bottom, 0));
  overflow: hidden;
}

@media (max-width: 640px) {
  .app-shell__main--fill {
    padding: 0.35rem 0.5rem;
    padding-bottom: max(0.35rem, env(safe-area-inset-bottom, 0));
  }
}

.route-view--fill {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>
